import React from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { Availability } from './pages/Availability';
import { AvailabilityManagement } from './pages/AvailabilityManagement';
import { Bookings } from './pages/Bookings';
import { BookAppointment } from './pages/BookAppointment';
import { Notifications } from './pages/Notifications';
import { NotificationSettings } from './pages/NotificationSettings';
import { EditProfile } from './pages/EditProfile';
import { Invite } from './pages/Invite';
import { LoadingOverlay } from './components/LoadingOverlay';
import { LoadingProvider } from './context/LoadingContext';
import { SignupProvider } from './context/SignupContext';
import { theme } from './theme/theme';
import { ThemeProvider } from '@mui/material';
import { ForgotPasswordPage } from './pages/new/ForgotPasswordPage';
import { ChangePassword } from './pages/ChangePassword';
import { VideoCall } from './pages/VideoCall';
import TeamsVideoCall from './pages/TeamsVideoCall';
import { PaymentHistory } from './pages/PaymentHistory';
import { PaymentHistoryList } from './pages/PaymentHistoryList';
import { MantineProvider } from '@mantine/core';
import '@mantine/core/styles.css';
import '@mantine/carousel/styles.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SingleSignOnOptions } from './pages/login/SingleSignOnOptions';
import { LoginFlowType, SignUpLogin } from './pages/login/SignUpEmail';
import { ForgotPassword } from './pages/login/ForgotPassword';
import { PageRoutes } from './constants/routes';
import { LocalStorageKeys } from './constants/locationStorage';
import { VerifyEmail } from './pages/login/VerifyEmail';
import { BestDescribes } from './pages/login/BestDescribes';
import { ConfirmDetails } from './pages/login/ConfirmDetails';
import { CategorySelection } from './pages/login/Categories';
import { OnboardingProvider } from './pages/login/SignUpContext';
import { SearchTerm } from './pages/login/SearchTerms';
import { AppProvider } from './context/AppContext';
import { AuthProvider } from './auth/AuthContext';
import { NewSearch } from './pages/new/SearchPage';
import { mantineTheme } from './theme/MantineTheme';
import { SearchResultsPage } from './pages/new/SearchResultsPage';
import { ProfilePage } from './pages/new/Profile/ProfilePage';
import { DailyVideoCallPage } from './pages/new/DailyVideoCall';
import { AppleCallback } from './pages/login/AppleCallback';
import { SharedProfileEntry } from './pages/SharedProfileEntry';
import { useAuth } from './auth/AuthContext';
import { SingleBookingPage } from './pages/new/SingleBookingPage';
import { FollowingPage } from './pages/new/Profile/FollowingPage';
import { TopicProvider } from './context/TopicContext';

// Add this guard component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  if (isAuthenticated) return <>{children}</>;

  const returnTo = location.pathname + location.search + location.hash;
  localStorage.setItem(LocalStorageKeys.POST_LOGIN_REDIRECT, returnTo);
  return (
    <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />
  );
};

// Redirect authenticated users away from login/signup pages
const PublicOnlyRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading, profileConfirmed } = useAuth();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated || !profileConfirmed) return <>{children}</>;

  const returnTo = searchParams.get('returnTo');
  return <Navigate to={returnTo ?? PageRoutes.SEARCH} replace />;
};

const queryClient = new QueryClient();

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <MantineProvider theme={mantineTheme}>
        <QueryClientProvider client={queryClient}>
          <LoadingProvider>
            <BrowserRouter>
              <SignupProvider>
                <LoadingOverlay />
                <AuthProvider>
                  <AppProvider>
                    <TopicProvider>
                      <OnboardingProvider>
                        <Routes>
                        <Route
                          path={PageRoutes.LOGIN}
                          element={
                            <PublicOnlyRoute>
                              <SingleSignOnOptions />
                            </PublicOnlyRoute>
                          }
                        />
                        <Route
                          path={PageRoutes.EMAIL_SIGNUP}
                          element={
                            <PublicOnlyRoute>
                              <SignUpLogin flowType={LoginFlowType.SIGN_UP} />
                            </PublicOnlyRoute>
                          }
                        />
                        <Route
                          path={PageRoutes.EMAIL_LOGIN}
                          element={
                            <PublicOnlyRoute>
                              <SignUpLogin flowType={LoginFlowType.LOG_IN} />
                            </PublicOnlyRoute>
                          }
                        />
                        <Route
                          path={PageRoutes.EMAIL_SIGNUP_VERIFY}
                          element={<VerifyEmail />}
                        />
                        <Route
                          path={PageRoutes.ONBOARDING_BEST_DESCRIBES}
                          element={<BestDescribes />}
                        />
                        <Route
                          path={PageRoutes.ONBOARDING_YOUR_DETAILS}
                          element={<ConfirmDetails />}
                        />
                        <Route
                          path={PageRoutes.ONBOARDING_CATEGORIES}
                          element={<CategorySelection />}
                        />
                        <Route
                          path={PageRoutes.ONBOARDING_SEARCH_TERMS}
                          element={<SearchTerm />}
                        />
                        <Route
                          path="/"
                          element={<Navigate to="/login" replace />}
                        />
                        <Route
                          path="/forgot-password"
                          element={<ForgotPasswordPage />}
                        />
                        <Route
                          path={PageRoutes.FORGOT_PASSWORD}
                          element={<ForgotPassword />}
                        />
                        <Route
                          path="/change-password"
                          element={<ChangePassword />}
                        />
                        <Route
                          path="/auth/apple/callback"
                          element={<AppleCallback />}
                        />
                        <Route
                          path="/search"
                          element={
                            <ProtectedRoute>
                              <NewSearch />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/search-results"
                          element={
                            <ProtectedRoute>
                              <SearchResultsPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/availability"
                          element={
                            <ProtectedRoute>
                              <Availability />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/bookings"
                          element={
                            <ProtectedRoute>
                              <Bookings />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/notifications"
                          element={
                            <ProtectedRoute>
                              <Notifications />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path={PageRoutes.NOTIFICATION_SETTINGS}
                          element={
                            <ProtectedRoute>
                              <NotificationSettings />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/profile/*"
                          element={
                            <ProtectedRoute>
                              <ProfilePage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/following"
                          element={
                            <ProtectedRoute>
                              <FollowingPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/edit-profile"
                          element={
                            <ProtectedRoute>
                              <EditProfile />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/availability-management"
                          element={
                            <ProtectedRoute>
                              <AvailabilityManagement />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/book-appointment/"
                          element={
                            <ProtectedRoute>
                              <BookAppointment />
                            </ProtectedRoute>
                          }
                        ></Route>
                        <Route
                          path="/video-call"
                          element={
                            <ProtectedRoute>
                              <VideoCall />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/teams-video-call"
                          element={
                            <ProtectedRoute>
                              <TeamsVideoCall />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/daily-video-call"
                          element={
                            <ProtectedRoute>
                              <DailyVideoCallPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/payment-history"
                          element={
                            <ProtectedRoute>
                              <PaymentHistory />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/payment-history-list"
                          element={
                            <ProtectedRoute>
                              <PaymentHistoryList
                                transactionType={'Received'}
                                year={''}
                                month={''}
                              />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/cb/:sharedHandle"
                          element={<SharedProfileEntry />}
                        />
                        <Route
                          path="/invite/:bookingId"
                          element={
                            <ProtectedRoute>
                              <Invite />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/booking/:id"
                          element={
                            <ProtectedRoute>
                              <SingleBookingPage />
                            </ProtectedRoute>
                          }
                        />
                        </Routes>
                      </OnboardingProvider>
                    </TopicProvider>
                  </AppProvider>
                </AuthProvider>
              </SignupProvider>
            </BrowserRouter>
          </LoadingProvider>
        </QueryClientProvider>
      </MantineProvider>
    </ThemeProvider>
  );
};

export default App;
